import { useEffect, useState } from "react";
import { Badge, Space, Button, Popconfirm, message, Col, Input, Row, Select } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import Table, { ColumnsType } from "antd/lib/table";
import { DeleteFilled } from "@ant-design/icons";
import { addSpTokenPool, deleteSpTokenPool, getSpTokenPoolList, validateSpTokenAuth } from "services/sp-token-pool";
import { convertToDate, getIdFromObjectId } from "utils";
import { Helmet } from "react-helmet";

interface DataType {
  _id: string;
  tokenId: string;
  regionShortName: string;
  userId: string;
  createdAt: string | null;
  expiredIn: string | null;
  valid: boolean;
}

const initialInputs = {
  tokenId: '',
  userId: '',
}

const SPTokenPool: React.FC = () => {
  const [dataTable, setDataTable] = useState<DataType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [inputs, setInputs] = useState<any>(initialInputs)

  const handleOnAdd = (params: any) => {
    setLoading(true)
    return addSpTokenPool(params)
      .then(({ data }: any) => {
        setDataTable((prev: DataType[]) => [
          {
            ...data.data,
            _id: getIdFromObjectId(data.data._id),
            userId: getIdFromObjectId(data.data.userId),
            tokenId: getIdFromObjectId(data.data.tokenId),
          },
          ...prev
        ]);
        message.success("Added Token pool successfully")
      })
      .catch(() => message.error("Cannot add Token pool"))
      .finally(() =>
        setLoading(() => {
          setInputs(initialInputs)
          return false
        })
      )
  }

  const handleOnDelete = (id: string) => {
    setLoading(true)
    return deleteSpTokenPool(id)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted Token pool successfully")
      })
      .catch(() => message.error("Cannot delete Token pool"))
      .finally(() => setLoading(false))
  }

  const handleOnValidateToken = (record: DataType) => {
    setLoading(true)
    const params = {
      userId: record.userId
    }
    return validateSpTokenAuth(record.tokenId, params)
      .then(({ data }: any) => {
        const newData = dataTable.map((d: any) => d._id === record._id ? ({...d, expiredIn: data.data.expiredIn, valid: data.data.valid}) : d);
        setDataTable(newData);
        message.success("Validating Token pool successfully")
      })
      .catch(() => message.error("Cannot validate Token pool"))
      .finally(() => setLoading(false))
  }

  const handleOnGetList = () => {
    return getSpTokenPoolList()
      .then(({ data }: any) =>
        setDataTable(() => data.data.map((d: DataType) => ({
          ...d,
          tokenId: getIdFromObjectId(d.tokenId),
          userId: getIdFromObjectId(d.userId),
          _id: getIdFromObjectId(d._id)
        })))
      )
      .catch(() => message.error('Cannot get list of SP Token pool'))
      .finally(() => setLoading(false))
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'Token ID',
      dataIndex: 'tokenId',
      key: 'tokenId',
    },
    {
      title: 'Region',
      dataIndex: 'regionShortName',
      key: 'regionShortName',
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => convertToDate(getIdFromObjectId(value))
    },
    {
      title: 'Expired In',
      dataIndex: 'expiredIn',
      key: 'expiredIn',
      render: value => convertToDate(getIdFromObjectId(value))
    },
    {
      title: 'Is Valid',
      dataIndex: 'valid',
      key: 'valid',
      render: value => (
        <Badge
          text={value ? "Yes" : "No"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => handleOnValidateToken(record)}>Validate</Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this ASIN?"
            onConfirm={() => handleOnDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" icon={<DeleteFilled />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  
  useEffect(() => {
    setLoading(true)
    handleOnGetList()
  }, [])

  return (
  <>
    <Helmet>
      <title>SP Token Pool</title>
    </Helmet>
    <PageHeader
      className="page-header"
      backIcon={true}
      title="SP Token Pool Management"
      footer={
        <>
          <Input.Group style={{ marginBottom: 15 }}>
            <Row gutter={8}>
              <Col span={6}>
                <Input
                  value={inputs.tokenId}
                  onChange={(e) => setInputs((prev: any) => ({ ...prev, tokenId: e.target.value }))}
                  placeholder="Enter Token Id (required)"
                />
              </Col>
              <Col span={6}>
                <Input
                  value={inputs.userId}
                  onChange={(e) => setInputs((prev: any) => ({ ...prev, userId: e.target.value }))}
                  placeholder="Enter User Id (required)"
                />
              </Col>
              <Col span={6}>
                <Button type="primary" disabled={!inputs.tokenId || !inputs.userId} onClick={() => handleOnAdd(inputs)}>Add</Button>
              </Col>
            </Row>
          </Input.Group>
          <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
        </>
      }
    />
  </>
  )
}

export default SPTokenPool;